/* SaveToHealthIntelligenceButton — универсален "⭐ Запази в Health Intelligence"
 * бутон, mountable където и да е в платформата (PlateSimulator, MealLog,
 * VAIS card, Lab panels, Digital Twin, и т.н.).
 *
 * Props:
 *   - snapshotType: "meal_plate" | "lab_panel" | "vais" | "digital_twin" |
 *                   "daily_summary" | "supplement" | "favorite_food"
 *   - title:    кратко заглавие за snapshot-а (макс. 200 chars)
 *   - summary:  1 изречение обобщение (макс. 500 chars)
 *   - payload:  free-form обект с цялата важна информация (NIS%, DIL, биомаркери)
 *   - tags:     ["high-fiber", "PCOS-friendly"]
 *   - size:     "sm" | "md" (default md)
 *
 * iter420 Phase 1 — MVP, без GDPR consent dialog (consent is implicit от click-а).
 * В Phase 3 ще добавим explicit consent dialog за sensitive medical data.
 */
import React, { useState } from "react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import { Bookmark, BookmarkCheck, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

export default function SaveToHealthIntelligenceButton({
  snapshotType,
  title,
  summary,
  payload,
  tags = [],
  notes,
  size = "md",
  className = "",
  testId = "save-to-hi-btn",
  onSaved,
}) {
  const { API, user } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    // iter460 — Removed pre-check that blocked cookie-authenticated users.
    // Cookie auth doesn't set axios.defaults Authorization, and `user` from
    // useAuth can be transiently null during refresh. We now always attempt
    // the save and let the backend's 401 (caught below) handle real auth failures.
    if (!snapshotType || !title) {
      toast.error(bg ? "Липсват задължителни данни" : "Missing required data");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/health-intelligence/snapshot`, {
        snapshot_type: snapshotType,
        title: String(title).slice(0, 200),
        summary: summary ? String(summary).slice(0, 500) : null,
        payload: payload || {},
        tags: Array.isArray(tags) ? tags.slice(0, 20) : [],
        notes: notes ? String(notes).slice(0, 1000) : null,
      });
      setSaved(true);
      toast.success(bg
        ? "Запазено в Health Intelligence ✓"
        : "Saved to Health Intelligence ✓");
      onSaved?.(data);
      // Reset "saved" state after 3s so user can save another similar snapshot
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      // iter425 — Specific BG message при 401 (token expired)
      if (e.response?.status === 401) {
        toast.error(bg ? "Сесията е изтекла. Моля, влезте отново." : "Session expired. Please log in.");
      } else {
        toast.error(e.response?.data?.detail || (bg ? "Грешка при запазване" : "Failed to save"));
      }
    } finally {
      setSaving(false);
    }
  };

  const isSmall = size === "sm";
  const Icon = saved ? BookmarkCheck : Bookmark;
  const label = saved
    ? (bg ? "Запазено" : "Saved")
    : (bg ? "Запази в Health Intelligence" : "Save to Health Intelligence");

  return (
    <Button
      onClick={handleSave}
      disabled={saving || saved}
      data-testid={testId}
      variant={saved ? "secondary" : "outline"}
      size={isSmall ? "sm" : "default"}
      className={`inline-flex items-center gap-1.5 font-semibold transition-all ${
        saved
          ? (isDark ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/20"
                    : "bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-50")
          : (isDark ? "border-indigo-500/40 text-indigo-300 hover:bg-indigo-500/10"
                    : "border-indigo-300 text-indigo-700 hover:bg-indigo-50")
      } ${className}`}
    >
      {saving ? (
        <Loader2 className={`${isSmall ? "w-3.5 h-3.5" : "w-4 h-4"} animate-spin`} />
      ) : (
        <Icon className={isSmall ? "w-3.5 h-3.5" : "w-4 h-4"} />
      )}
      {label}
    </Button>
  );
}
